import { Button, Card, Input, Table, Tag } from "@arco-design/web-react";
import type { TableColumnProps } from "@arco-design/web-react";
import { MoveRight, Pencil, Plus, Power, Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";

import { useCurrentAuthUser } from "../../auth";
import { canUsePermission } from "../../../lib/auth";
import {
  crmOrganizationUnitPageSizeOptions,
  crmOrganizationUnitTypeOptions,
} from "../config";
import {
  emptyCrmOrganizationUnitMutation,
  useCrmOrganizationUnitRuntime,
  type CrmOrganizationUnitDeletePreview,
} from "../runtime/useCrmOrganizationUnitRuntime";
import type {
  CrmOrganizationUnitItem,
  CrmOrganizationUnitMutation,
  CrmOrganizationUnitTreeNode,
  CrmOrganizationUnitTreeRow,
} from "../types";
import "./CrmOrganizationUnitPage.css";
import "../../../shared/ui/crmListPageLayout.css";

type UnitModalState =
  | {
      type: "create";
      parent: CrmOrganizationUnitTreeRow;
      unitType: CrmOrganizationUnitMutation["unitType"];
    }
  | { type: "edit"; item: CrmOrganizationUnitItem }
  | { type: "move"; item: CrmOrganizationUnitItem };

type MoveParentCandidate = {
  key: string;
  label: string;
  labelText: string;
  parentUnitId?: number;
  nextUnitType: CrmOrganizationUnitMutation["unitType"];
  depth: number;
};

/** 组织单元页面主体。 */
export function CrmOrganizationUnitFeaturePage() {
  const runtime = useCrmOrganizationUnitRuntime();
  const currentUser = useCurrentAuthUser().data;
  const canManage = canUsePermission(currentUser, "system.organization:manage");
  const canDelete = canUsePermission(currentUser, "system.organization:delete");
  const [unitModalState, setUnitModalState] = useState<UnitModalState | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<(string | number)[]>([]);
  const defaultExpandedKeySignature = runtime.defaultExpandedRowKeys.join("|");
  const columns = useMemo(
    () =>
      buildOrganizationUnitColumns({
        canManage,
        canDelete,
        onEdit: (item) => setUnitModalState({ type: "edit", item }),
        onMove: (item) => {
          runtime.setMoveTargetItem(item);
          setUnitModalState({ type: "move", item });
        },
        onCreateChild: (row, unitType) => setUnitModalState({ type: "create", parent: row, unitType }),
        onToggleDisabled: runtime.handleToggleDisabled,
        onDelete: runtime.handleRequestDelete,
      }),
    [canDelete, canManage, runtime.handleToggleDisabled, runtime.handleRequestDelete],
  );

  useEffect(() => {
    setExpandedRowKeys(runtime.defaultExpandedRowKeys);
  }, [defaultExpandedKeySignature]);

  return (
    <main className="crm-organization-unit-page">
      <Card className="crm-organization-unit-card" bodyStyle={{ padding: 0 }}>
        <section
          className="crm-list-filter-panel crm-organization-unit-filter-panel"
          role="search"
          aria-label="组织单元筛选"
          data-layout="two-tier-responsive"
        >
          <div className="crm-list-filter-fields crm-organization-unit-filter-grid">
          <div className="crm-list-filter-primary">
            <Field label="省" size="sm">
              <Input
                value={runtime.draftFilters.province ?? ""}
                onChange={(province) => runtime.setDraftFilters({ ...runtime.draftFilters, province })}
              />
            </Field>
            <Field label="市" size="sm">
              <Input
                value={runtime.draftFilters.city ?? ""}
                onChange={(city) => runtime.setDraftFilters({ ...runtime.draftFilters, city })}
              />
            </Field>
            <Field label="学校名称/拼音码" size="lg">
              <Input
                placeholder="学校名称或拼音码"
                value={runtime.draftFilters.schoolKeyword ?? ""}
                onChange={(schoolKeyword) =>
                  runtime.setDraftFilters({
                    ...runtime.draftFilters,
                    schoolKeyword,
                    schoolSpellCode: undefined,
                  })
                }
              />
            </Field>
            <Field label="节点名称" size="md">
              <Input
                placeholder="学院、系或专业名称"
                value={runtime.draftFilters.unitKeyword ?? ""}
                onChange={(unitKeyword) =>
                  runtime.setDraftFilters({ ...runtime.draftFilters, unitKeyword })
                }
              />
            </Field>
            <Field label="节点类型" size="md">
              <select
                aria-label="节点类型"
                className="crm-organization-unit-native-select"
                value={runtime.draftFilters.unitType ?? ""}
                onChange={(event) =>
                  runtime.setDraftFilters({
                    ...runtime.draftFilters,
                    unitType: event.target.value
                      ? (event.target.value as CrmOrganizationUnitMutation["unitType"])
                      : undefined,
                  })
                }
              >
                <option value="">全部</option>
                {crmOrganizationUnitTypeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="状态" size="sm">
              <select
                aria-label="启停状态"
                className="crm-organization-unit-native-select"
                value={
                  runtime.draftFilters.isDisabled === undefined
                    ? ""
                    : String(runtime.draftFilters.isDisabled)
                }
                onChange={(event) =>
                  runtime.setDraftFilters({
                    ...runtime.draftFilters,
                    isDisabled:
                      event.target.value === "" ? undefined : event.target.value === "true",
                  })
                }
              >
                <option value="">全部</option>
                <option value="false">启用</option>
                <option value="true">停用</option>
              </select>
            </Field>
          </div>

          {runtime.errorMessage ? (
            <div className="crm-organization-unit-error">{runtime.errorMessage}</div>
          ) : null}
          </div>
          <div className="crm-list-filter-actions">
            <Button onClick={runtime.handleReset}>重置</Button>
            <Button type="primary" onClick={runtime.handleSearch}>
              查询
            </Button>
          </div>
        </section>
      </Card>

      <Card className="crm-organization-unit-card" bodyStyle={{ padding: 0 }}>
        <div className="crm-organization-unit-table-header">
          <div>
            <h2>校院系专业树</h2>
            <p>建议先按学校名称或拼音码定位学校，再展开维护校院系专业树。</p>
          </div>
          <span>共 {runtime.total} 个组织节点</span>
        </div>
        <Table
          rowKey="key"
          className="crm-organization-unit-tree-table"
          columns={columns}
          data={runtime.treeRows}
          loading={runtime.loading}
          border={false}
          indentSize={24}
          expandedRowKeys={expandedRowKeys}
          onExpandedRowsChange={setExpandedRowKeys}
          noDataElement="暂无组织单元数据"
          scroll={{ x: 1120 }}
          pagination={
            runtime.isCompleteTreeMode
              ? false
              : {
                  current: runtime.page,
                  pageSize: runtime.pageSize,
                  total: runtime.total,
                  sizeCanChange: true,
                  sizeOptions: crmOrganizationUnitPageSizeOptions,
                  showTotal: true,
                  onChange: runtime.handlePageChange,
                  onPageSizeChange: runtime.handlePageSizeChange,
                }
          }
        />
      </Card>

      <UnitMutationModal
        state={unitModalState}
        errorMessage={runtime.mutationErrorMessage}
        saving={runtime.saving}
        onClose={() => setUnitModalState(null)}
        onCreate={runtime.handleCreate}
        onUpdate={runtime.handleUpdate}
      />
      <MoveUnitModal
        state={unitModalState}
        tree={runtime.moveTree}
        loading={runtime.moveTreeLoading}
        treeErrorMessage={runtime.moveTreeErrorMessage}
        errorMessage={runtime.mutationErrorMessage}
        saving={runtime.saving}
        onClose={() => {
          runtime.setMoveTargetItem(null);
          setUnitModalState(null);
        }}
        onUpdate={runtime.handleUpdate}
      />
      <DeletePreviewModal
        preview={runtime.deletePreview}
        errorMessage={runtime.mutationErrorMessage}
        saving={runtime.saving}
        onClose={() => runtime.setDeletePreview(null)}
        onConfirm={runtime.handleConfirmDelete}
      />
    </main>
  );
}

/** 构建组织单元树形表格列。 */
function buildOrganizationUnitColumns({
  canManage,
  canDelete,
  onEdit,
  onMove,
  onCreateChild,
  onToggleDisabled,
  onDelete,
}: {
  canManage: boolean;
  canDelete: boolean;
  onEdit: (item: CrmOrganizationUnitItem) => void;
  onMove: (item: CrmOrganizationUnitItem) => void;
  onCreateChild: (
    row: CrmOrganizationUnitTreeRow,
    unitType: CrmOrganizationUnitMutation["unitType"],
  ) => void;
  onToggleDisabled: (item: CrmOrganizationUnitItem) => void;
  onDelete: (item: CrmOrganizationUnitItem) => void;
}): TableColumnProps<CrmOrganizationUnitTreeRow>[] {
  return [
    {
      title: "组织结构",
      dataIndex: "unitName",
      width: 320,
      render: (_, row) => (
        <div className={resolveTreeCellClassName(row)}>
          <strong>{row.unitName}</strong>
          <small>
            {row.isSchoolRoot
              ? "学校根节点"
              : row.sourceItem
                ? formatUnitPathForDisplay(row.sourceItem.unitPathName || row.sourceItem.unitName)
                : "按路径补齐的祖先节点"}
          </small>
        </div>
      ),
    },
    {
      title: "学校/地区",
      width: 230,
      render: (_, row) => (
        <div className="crm-organization-unit-school-cell">
          <strong>{row.schoolName}</strong>
          <small>{[row.province, row.city, row.schoolSpellCode].filter(Boolean).join(" / ") || "--"}</small>
        </div>
      ),
    },
    {
      title: "节点类型",
      dataIndex: "unitType",
      width: 120,
      render: (_, row) => <Tag color={resolveUnitTypeColor(row.unitType)}>{row.unitType}</Tag>,
    },
    {
      title: "启停",
      width: 90,
      render: (_, row) =>
        row.sourceItem ? (
          <Tag color={row.isDisabled ? "orangered" : "green"}>{row.isDisabledLabel}</Tag>
        ) : (
          "--"
        ),
    },
    {
      title: "联系人",
      dataIndex: "contactCount",
      width: 90,
      render: (_, row) => row.contactCount ?? "--",
    },
    {
      title: "客户",
      dataIndex: "customerCount",
      width: 90,
      render: (_, row) => row.customerCount ?? "--",
    },
    {
      title: "下级",
      dataIndex: "childCount",
      width: 90,
      render: (_, row) => row.childCount ?? row.children?.length ?? "--",
    },
    {
      title: "更新时间",
      dataIndex: "updatedAt",
      width: 170,
      render: (_, row) => row.updatedAt ?? "--",
    },
    {
      title: "操作",
      fixed: "right",
      width: 220,
      render: (_, row) => {
        const childUnitType = resolveChildUnitType(row);
        return (
          <div className="crm-organization-unit-row-actions">
            {childUnitType ? (
              <Button
                aria-label={`新增下级节点 ${row.unitName}`}
                type="text"
                icon={<Plus size={15} />}
                disabled={!canManage}
                onClick={() => onCreateChild(row, childUnitType)}
              />
            ) : null}
            {row.sourceItem ? (
              <>
                <Button
                  aria-label={`编辑组织单元 ${row.sourceItem.unitName}`}
                  type="text"
                  icon={<Pencil size={15} />}
                  disabled={!canManage}
                  onClick={() => onEdit(row.sourceItem!)}
                />
                <Button
                  aria-label={`移动组织单元 ${row.sourceItem.unitName}`}
                  type="text"
                  icon={<MoveRight size={15} />}
                  disabled={!canManage}
                  onClick={() => onMove(row.sourceItem!)}
                />
                <Button
                  aria-label={`${row.sourceItem.isDisabled ? "启用" : "停用"}组织单元 ${row.sourceItem.unitName}`}
                  type="text"
                  icon={<Power size={15} />}
                  disabled={!canManage}
                  onClick={() => onToggleDisabled(row.sourceItem!)}
                />
                <Button
                  aria-label={`删除组织单元 ${row.sourceItem.unitName}`}
                  type="text"
                  status="danger"
                  icon={<Trash2 size={15} />}
                  disabled={!canDelete}
                  onClick={() => onDelete(row.sourceItem!)}
                />
              </>
            ) : null}
            {!childUnitType && !row.sourceItem ? <span className="crm-organization-unit-muted">--</span> : null}
          </div>
        );
      },
    },
  ];
}

/** 叶子节点补齐树形展开图标占位，避免视觉上比同级可展开节点少缩进一格。 */
function resolveTreeCellClassName(row: CrmOrganizationUnitTreeRow) {
  return [
    "crm-organization-unit-tree-cell",
    !row.isSchoolRoot && !row.children?.length ? "crm-organization-unit-tree-cell--leaf" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

/** 根据当前节点类型判断可新增的下级类型。 */
function resolveChildUnitType(
  row: CrmOrganizationUnitTreeRow,
): CrmOrganizationUnitMutation["unitType"] | undefined {
  if (row.isSchoolRoot) {
    return "二级学院";
  }
  if (!row.sourceItem) {
    return undefined;
  }
  if (row.sourceItem.unitType === "二级学院") {
    return "系";
  }
  if (row.sourceItem.unitType === "系") {
    return "专业";
  }
  return undefined;
}

/** 将后端真实组织路径转换为页面可读的短路径，不按固定三段层级强行补齐。 */
function formatUnitPathForDisplay(unitPathName: string) {
  return unitPathName
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean)
    .join("-");
}

/** 组织单元新增与编辑弹窗。 */
function UnitMutationModal({
  state,
  errorMessage,
  saving,
  onClose,
  onCreate,
  onUpdate,
}: {
  state: UnitModalState | null;
  errorMessage: string;
  saving: boolean;
  onClose: () => void;
  onCreate: (payload: CrmOrganizationUnitMutation) => Promise<void>;
  onUpdate: (item: CrmOrganizationUnitItem, payload: CrmOrganizationUnitMutation) => Promise<void>;
}) {
  const [payload, setPayload] = useState<CrmOrganizationUnitMutation>(emptyCrmOrganizationUnitMutation);

  useEffect(() => {
    if (!state) {
      return;
    }
    if (state.type === "move") {
      return;
    }
    if (state.type === "edit") {
      setPayload(buildMutationPayloadFromItem(state.item));
      return;
    }
    setPayload({
      ...emptyCrmOrganizationUnitMutation,
      orgId: state.parent.orgId,
      parentUnitId: state.parent.sourceItem?.id,
      unitType: state.unitType,
    });
  }, [state]);

  /** 提交组织单元表单。 */
  async function handleOk() {
    if (!state) {
      return;
    }
    if (state.type === "create") {
      await onCreate(payload);
    } else if (state.type === "edit") {
      await onUpdate(state.item, {
        ...payload,
        orgId: state.item.orgId,
        parentUnitId: state.item.parentUnitId,
        unitType: state.item.unitType as CrmOrganizationUnitMutation["unitType"],
        orgStatus: state.item.orgStatus as CrmOrganizationUnitMutation["orgStatus"],
        effectiveFrom: state.item.effectiveFrom,
        effectiveTo: state.item.effectiveTo,
        isDisabled: state.item.isDisabled,
      });
    }
    onClose();
  }

  const isCreate = state?.type === "create";

  return (
    <LocalModal
      title={isCreate ? `新增${state.unitType}` : "编辑组织单元"}
      visible={state?.type === "create" || state?.type === "edit"}
      onCancel={onClose}
      onOk={handleOk}
      okText={saving ? "保存中..." : "保存"}
      cancelText="取消"
      okDisabled={saving || payload.orgId <= 0 || !payload.unitName.trim()}
    >
      {errorMessage ? <div className="crm-organization-unit-error">{errorMessage}</div> : null}
      {isCreate ? (
        <div className="crm-organization-unit-form-context">
          上级：{state.parent.unitName} / 新增类型：{state.unitType}
        </div>
      ) : null}
      <div className="crm-organization-unit-form-grid">
        <Field label="节点名称">
          <Input
            aria-label="节点名称"
            value={payload.unitName}
            onChange={(unitName) => setPayload({ ...payload, unitName })}
          />
        </Field>
        <Field label="备注">
          <Input.TextArea
            aria-label="备注"
            value={payload.remark ?? ""}
            onChange={(remark) => setPayload({ ...payload, remark })}
          />
        </Field>
      </div>
      <p className="crm-organization-unit-form-hint">
        学校与父节点由树上操作自动带入；如需调整归属，请使用独立的移动节点功能。
      </p>
    </LocalModal>
  );
}

/** 独立移动组织单元弹窗，避免编辑表单暴露学校 ID 和父节点 ID。 */
function MoveUnitModal({
  state,
  tree,
  loading,
  treeErrorMessage,
  errorMessage,
  saving,
  onClose,
  onUpdate,
}: {
  state: UnitModalState | null;
  tree?: CrmOrganizationUnitTreeNode;
  loading: boolean;
  treeErrorMessage: string;
  errorMessage: string;
  saving: boolean;
  onClose: () => void;
  onUpdate: (item: CrmOrganizationUnitItem, payload: CrmOrganizationUnitMutation) => Promise<void>;
}) {
  const [selectedParentKey, setSelectedParentKey] = useState("");
  const item = state?.type === "move" ? state.item : undefined;
  const candidates = useMemo(
    () => (item && tree ? resolveMoveParentCandidates(item, tree) : []),
    [item, tree],
  );
  const candidateByKey = useMemo(
    () => new Map(candidates.map((candidate) => [candidate.key, candidate])),
    [candidates],
  );

  useEffect(() => {
    if (state?.type !== "move") {
      setSelectedParentKey("");
      return;
    }
    setSelectedParentKey("");
  }, [state]);

  /** 提交组织单元移动。 */
  async function handleOk() {
    if (!item) {
      return;
    }
    const candidate = candidates.find((entry) => entry.key === selectedParentKey);
    if (!candidate) {
      return;
    }
    await onUpdate(item, {
      ...buildMutationPayloadFromItem(item),
      parentUnitId: candidate.parentUnitId,
      unitType: candidate.nextUnitType,
    });
    onClose();
  }

  return (
    <LocalModal
      title="移动组织单元"
      visible={Boolean(item)}
      onCancel={onClose}
      onOk={handleOk}
      okText={saving ? "移动中..." : "保存移动"}
      cancelText="取消"
      okDisabled={saving || !selectedParentKey}
    >
      {errorMessage || treeErrorMessage ? (
        <div className="crm-organization-unit-error">{errorMessage || treeErrorMessage}</div>
      ) : null}
      {item ? (
        <>
          <div className="crm-organization-unit-form-context">
            当前节点：{item.unitName} / 当前路径：{item.unitPathName || item.unitName}
          </div>
          <div className="crm-organization-unit-move-tree" role="tree" aria-label="目标父节点树">
            {loading ? <div className="crm-organization-unit-muted">正在加载学校组织树...</div> : null}
            {!loading && candidates.length === 0 ? (
              <div className="crm-organization-unit-blocked">
                当前学校组织树中没有可移动到的合法父节点。
              </div>
            ) : null}
            {!loading && tree
              ? renderMoveTargetTree({
                  tree,
                  candidateByKey,
                  selectedParentKey,
                  onSelect: setSelectedParentKey,
                })
              : null}
          </div>
          <p className="crm-organization-unit-form-hint">
            专业可移动到同校任意二级学院或系下；其他节点只允许同级或向下，不能上移。
          </p>
        </>
      ) : null}
    </LocalModal>
  );
}

/** 渲染完整学校组织树，非候选节点保留为禁用锚点，避免候选父节点变成平铺列表。 */
function renderMoveTargetTree({
  tree,
  candidateByKey,
  selectedParentKey,
  onSelect,
}: {
  tree: CrmOrganizationUnitTreeNode;
  candidateByKey: Map<string, MoveParentCandidate>;
  selectedParentKey: string;
  onSelect: (key: string) => void;
}) {
  return renderMoveTargetTreeNode({
    node: tree,
    depth: 0,
    candidateByKey,
    selectedParentKey,
    onSelect,
  });
}

/** 渲染移动目标树节点；专业节点不能作为父节点，因此仅作为必要祖先时保留。 */
function renderMoveTargetTreeNode({
  node,
  depth,
  candidateByKey,
  selectedParentKey,
  onSelect,
}: {
  node: CrmOrganizationUnitTreeNode;
  depth: number;
  candidateByKey: Map<string, MoveParentCandidate>;
  selectedParentKey: string;
  onSelect: (key: string) => void;
}): ReactNode {
  const children = node.children
    .map((child) =>
      renderMoveTargetTreeNode({
        node: child,
        depth: depth + 1,
        candidateByKey,
        selectedParentKey,
        onSelect,
      }),
    )
    .filter(Boolean);
  const candidate = candidateByKey.get(String(node.id));
  const shouldDisplayNode = node.isSchoolRoot || candidate || node.unitType === "二级学院" || node.unitType === "系";
  if (!shouldDisplayNode && children.length === 0) {
    return null;
  }

  const label = candidate?.label ?? `${node.title}（${node.isSchoolRoot ? "学校" : node.unitType}）`;
  const isSelected = Boolean(candidate && selectedParentKey === candidate.key);

  return (
    <div key={`${node.isSchoolRoot ? "school" : "unit"}-${node.id}`} role="none">
      {shouldDisplayNode ? (
        <button
          type="button"
          role="treeitem"
          aria-level={depth + 1}
          aria-disabled={!candidate}
          aria-selected={isSelected}
          aria-label={
            candidate ? `选择目标父节点 ${candidate.labelText}` : `不可选择目标父节点 ${node.title}`
          }
          className={[
            "crm-organization-unit-move-tree-node",
            isSelected ? "is-selected" : "",
            candidate ? "" : "is-disabled",
          ]
            .filter(Boolean)
            .join(" ")}
          style={{ paddingLeft: `${12 + depth * 22}px` }}
          onClick={() => {
            if (candidate) {
              onSelect(candidate.key);
            }
          }}
        >
          <span>{label}</span>
        </button>
      ) : null}
      {children}
    </div>
  );
}

/** 根据已有节点构造完整更新载荷，移动与编辑都复用同一更新接口。 */
function buildMutationPayloadFromItem(item: CrmOrganizationUnitItem): CrmOrganizationUnitMutation {
  return {
    orgId: item.orgId,
    parentUnitId: item.parentUnitId,
    unitType: item.unitType as CrmOrganizationUnitMutation["unitType"],
    unitName: item.unitName,
    orgStatus: item.orgStatus as CrmOrganizationUnitMutation["orgStatus"],
    effectiveFrom: item.effectiveFrom,
    effectiveTo: item.effectiveTo,
    isDisabled: item.isDisabled,
    remark: item.remark,
  };
}

/** 计算移动时可选父节点，避免把节点移动到更浅层级造成上移。 */
function resolveMoveParentCandidates(
  item: CrmOrganizationUnitItem,
  tree: CrmOrganizationUnitTreeNode,
): MoveParentCandidate[] {
  const flattenedNodes = flattenTreeNodes(tree);
  return flattenedNodes
    .map(({ node, depth }) => resolveMoveParentCandidate(item, node, depth))
    .filter((candidate): candidate is MoveParentCandidate => Boolean(candidate));
}

/** 判断某个树节点是否可作为移动目标父节点。 */
function resolveMoveParentCandidate(
  item: CrmOrganizationUnitItem,
  node: CrmOrganizationUnitTreeNode,
  depth: number,
): MoveParentCandidate | undefined {
  const nextUnitType = resolveMoveNextUnitType(item.unitType, node);
  if (!nextUnitType || isMovingUp(item.unitType, nextUnitType)) {
    return undefined;
  }
  if (nextUnitType !== item.unitType && item.childCount > 0) {
    return undefined;
  }
  if (node.isSchoolRoot || node.id === item.id || node.id === item.parentUnitId) {
    return undefined;
  }
  return {
    key: String(node.id),
    label: `${node.title}（${node.unitType}）`,
    labelText: node.title,
    parentUnitId: node.id,
    nextUnitType,
    depth,
  };
}

/** 判断目标层级是否会造成上移。 */
function isMovingUp(currentUnitType: string, nextUnitType: string) {
  return resolveUnitTypeDepth(nextUnitType) < resolveUnitTypeDepth(currentUnitType);
}

/** 组织单元固定深度权重。 */
function resolveUnitTypeDepth(unitType: string) {
  if (unitType === "二级学院") {
    return 1;
  }
  if (unitType === "系") {
    return 2;
  }
  if (unitType === "专业") {
    return 3;
  }
  return 0;
}
/** 根据被移动节点和目标父节点推导移动后的节点类型。 */
function resolveMoveNextUnitType(
  currentUnitType: string,
  node: CrmOrganizationUnitTreeNode,
): CrmOrganizationUnitMutation["unitType"] | undefined {
  if (node.isSchoolRoot) {
    return currentUnitType === "二级学院" ? "二级学院" : undefined;
  }
  if (currentUnitType === "专业" && (node.unitType === "二级学院" || node.unitType === "系")) {
    return "专业";
  }
  if (currentUnitType === "系" && node.unitType === "二级学院") {
    return "系";
  }
  if (currentUnitType === "系" && node.unitType === "系") {
    return "专业";
  }
  return undefined;
}

/** 扁平化学校完整组织树，保留层级用于树形缩进展示。 */
function flattenTreeNodes(
  tree: CrmOrganizationUnitTreeNode,
  depth = 0,
): Array<{ node: CrmOrganizationUnitTreeNode; depth: number }> {
  const result = [{ node: tree, depth }];
  for (const child of tree.children) {
    result.push(...flattenTreeNodes(child, depth + 1));
  }
  return result;
}

/** 删除预览弹窗。 */
function DeletePreviewModal({
  preview,
  errorMessage,
  saving,
  onClose,
  onConfirm,
}: {
  preview: CrmOrganizationUnitDeletePreview | null;
  errorMessage: string;
  saving: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const blocked = Boolean(preview && preview.blockedReasons.length > 0);
  return (
    <LocalModal
      title="删除组织单元"
      visible={Boolean(preview)}
      onCancel={onClose}
      onOk={blocked ? undefined : onConfirm}
      okText={saving ? "删除中..." : "确认删除"}
      cancelText={blocked ? "知道了" : "取消"}
      okDisabled={saving}
      footer={
        blocked ? (
          <Button type="primary" onClick={onClose}>
            知道了
          </Button>
        ) : undefined
      }
    >
      {errorMessage ? <div className="crm-organization-unit-error">{errorMessage}</div> : null}
      {preview ? (
        <div className="crm-organization-unit-delete-summary">
          <p>
            学校：<strong>{preview.item.schoolName}</strong>
          </p>
          <p>
            节点：<strong>{preview.item.unitPathName || preview.item.unitName}</strong>
          </p>
          <ul>
            <li>下级节点：{preview.item.childCount}</li>
            <li>联系人引用：{preview.item.contactCount}</li>
            <li>客户引用：{preview.item.customerCount}</li>
          </ul>
          {blocked ? (
            <div className="crm-organization-unit-blocked">
              当前节点不能物理删除：{preview.blockedReasons.join("、")}。建议改用停用。
            </div>
          ) : (
            <div className="crm-organization-unit-danger">
              当前节点无占用，确认后将物理删除，删除后不可恢复。
            </div>
          )}
        </div>
      ) : null}
    </LocalModal>
  );
}

/** 本功能域轻量弹窗。 */
function LocalModal({
  title,
  visible,
  onCancel,
  onOk,
  okText = "确定",
  cancelText = "取消",
  okDisabled = false,
  footer,
  children,
}: {
  title: ReactNode;
  visible: boolean;
  onCancel: () => void;
  onOk?: () => Promise<void> | void;
  okText?: string;
  cancelText?: string;
  okDisabled?: boolean;
  footer?: ReactNode;
  children: ReactNode;
}) {
  if (!visible) {
    return null;
  }

  return (
    <div className="crm-organization-unit-modal-backdrop" role="presentation">
      <section
        className="crm-organization-unit-modal"
        role="dialog"
        aria-modal="true"
        aria-label={String(title)}
      >
        <header className="crm-organization-unit-modal-header">
          <h2>{title}</h2>
          <Button type="text" onClick={onCancel}>
            关闭
          </Button>
        </header>
        <div className="crm-organization-unit-modal-body">{children}</div>
        <footer className="crm-organization-unit-modal-footer">
          {footer ?? (
            <>
              <Button onClick={onCancel}>{cancelText}</Button>
              {onOk ? (
                <Button type="primary" disabled={okDisabled} onClick={onOk}>
                  {okText}
                </Button>
              ) : null}
            </>
          )}
        </footer>
      </section>
    </div>
  );
}

/** 表单字段容器。 */
function Field({
  label,
  size = "md",
  children,
}: {
  label: string;
  size?: "sm" | "md" | "lg" | "xl";
  children: ReactNode;
}) {
  return (
    <label className={`crm-list-field crm-list-field--${size} crm-organization-unit-field`}>
      <span>{label}</span>
      {children}
    </label>
  );
}

/** 根据节点类型返回标签颜色。 */
function resolveUnitTypeColor(unitType: string) {
  if (unitType === "二级学院") {
    return "arcoblue";
  }
  if (unitType === "系") {
    return "purple";
  }
  return "green";
}
